import type { Ref } from 'vue';
import type { BlockEditorBounds } from '#layers/block-engine/composables/block-editor/bounds';
import type { BlockEditorChildParentMap } from '#layers/block-engine/composables/block-editor/child-parent-map';
import type { BlockEditorSelection } from '#layers/block-engine/composables/block-editor/selection';
import type { BlockEditorUpdateBlock } from '#layers/block-engine/composables/block-editor/update';
import type { BlockSerialized } from '#layers/block-engine/types/block';
import type { BlockEditorContext } from '#layers/block-engine/types/block-editor';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';
import { processRemoveBlock } from '#layers/block-engine/composables/block-editor/utils/process-remove-block';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';
import { isBlockLocked } from '#layers/block-engine/utils/block/lock';

export function useBlockEditorRemove({
	blockData,
	updateBlock,
	childParentMap,
	selection,
	bounds,
	getContext,
}: {
	blockData: Ref<BlockSerialized | null>;
	updateBlock: BlockEditorUpdateBlock;
	childParentMap: BlockEditorChildParentMap;
	selection: BlockEditorSelection;
	bounds: BlockEditorBounds;
	getContext: () => BlockEditorContext;
}) {
	const { getBlock } = useBlockLibraryStore();

	function _removeBlock(childId: string) {
		if (!blockData.value) {
			return;
		}

		const currentParent = childParentMap.value.get(childId);
		const child = blockData.value.children?.[childId];

		if (!child) {
			return;
		}

		// Check if the block is locked
		if (isBlockSerializedChild(child)) {
			const editorContext = getContext();
			const block = getBlock(child.blockId);

			if (block && isBlockLocked(child, block, editorContext)) {
				return;
			}
		}

		const { data } = processRemoveBlock({
			childId,
			parentChildId: currentParent?.parentId,
			slot: currentParent?.slot,
			editedBlock: blockData.value,
		});

		// Remove slot
		if (isBlockSerializedSlotOutlet(child) && blockData.value.slots) {
			const slotName = child.slotName;

			if (!Object.values(data.children ?? {}).some((c) => isBlockSerializedSlotOutlet(c) && c.slotName === slotName)) {
				data.slots = blockData.value.slots.filter((s) => s.name !== slotName);
			}
		}

		updateBlock(data);

		bounds.updateEvent.trigger(null);
	}

	/**
	 * Remove a block from the edited block
	 */
	function removeBlock(childId: string) {
		_removeBlock(childId);

		const index = selection.selectedChildIds.value.indexOf(childId);

		if (index !== -1) {
			selection.selectedChildIds.value.splice(index, 1);
		}
	}

	/**
	 * Remove the selected blocks
	 */
	function removeSelectedBlocks() {
		if (selection.selectedChildIds.value.length > 0) {
			for (const childId of selection.selectedChildIds.value) {
				_removeBlock(childId);
			}

			selection.clear();
		}
	}

	return {
		removeBlock,
		removeSelectedBlocks,
	};
}

export type BlockEditorRemoveBlock = ReturnType<typeof useBlockEditorRemove>['removeBlock'];
export type BlockEditorRemoveSelectedBlocks = ReturnType<typeof useBlockEditorRemove>['removeSelectedBlocks'];
